import mapboxgl from 'mapbox-gl';
import { SerializableTool } from './MapMarker';

interface CreateMapMarkerProps {
  tool: SerializableTool;
  map: mapboxgl.Map;
  color: string;
}

export const createMapMarker = ({ tool, map, color }: CreateMapMarkerProps): mapboxgl.Marker => {
  // Create popup content
  const popupContent = `
    <div class="p-4 max-w-sm">
      <div class="flex items-center gap-3 mb-2">
        ${tool.logo ? `<img src="${tool.logo}" alt="${tool.name}" class="w-8 h-8 rounded-full object-cover"/>` : ''}
        <h3 class="font-semibold text-gray-900">${tool.name}</h3>
      </div>
      <p class="text-sm text-gray-600 mb-2">${tool.description}</p>
      ${tool.address ? `
        <p class="text-sm text-gray-500 mb-2 flex items-center gap-1.5">
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          ${tool.address}
        </p>
      ` : ''}
      <div class="flex items-center gap-2 text-sm text-gray-500 mb-2">
        <span class="px-2 py-1 bg-gray-100 rounded-full">${tool.categories[0] || 'Other'}</span>
        ${tool.rating ? `<span>★ ${tool.rating.toFixed(1)}</span>` : ''}
        ${tool.reviews ? `<span>(${tool.reviews})</span>` : ''}
      </div>
      <div class="flex gap-2">
        ${tool.business_phone ? `
          <a href="tel:${tool.business_phone}" class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm hover:bg-green-200 transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
            Call
          </a>
        ` : ''}
        <a href="${tool.visitUrl}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm hover:bg-purple-200 transition-colors">
          Visit Website
        </a>
      </div>
    </div>
  `;

  // Create and add the marker
  const marker = new mapboxgl.Marker({
    color: color,
    scale: 0.8
  })
    .setLngLat([tool.longitude, tool.latitude])
    .setPopup(
      new mapboxgl.Popup({ 
        offset: 25,
        className: 'custom-popup'
      })
      .setHTML(popupContent)
    )
    .addTo(map);

  return marker;
};